import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ShareButtons from './components/ShareButtons';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

function App() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [, setTestId] = useState(null);
  // 检查是否是分享页面，如果是则跳过初始loading
  const [showInitialLoading, setShowInitialLoading] = useState(() => {
    const path = window.location.pathname;
    return !path.match(/\/share\/(.+)/);
  });
  const logoRef = useRef(null);

  // 检查是否是分享页面
  useEffect(() => {
    const path = window.location.pathname;
    const shareMatch = path.match(/\/share\/(.+)/);
    if (shareMatch) {
      const sharedTestId = shareMatch[1];
      setShowInitialLoading(false); // 分享页面跳过初始加载
      loadSharedResult(sharedTestId);
    }
  }, []);

  // 添加剪贴板粘贴监听器
  useEffect(() => {
    const handlePaste = (e) => {
      // 只在没有分析结果时允许粘贴
      if (analysisResult) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      // 查找图片类型的剪贴板项
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // 检查文件大小
            if (file.size > 5 * 1024 * 1024) {
              setError('图片大小不能超过5MB');
              return;
            }
            
            // 重置状态并设置新图片
            setUploadedImage(file);
            setAnalysisResult(null);
            setResultImageUrl(null);
            setError(null);
            setShareUrl(null);
            setTestId(null);
          }
          break;
        }
      }
    };

    // 添加全局粘贴监听器
    document.addEventListener('paste', handlePaste);
    
    // 清理监听器
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [analysisResult]);

  // 处理初始加载完成
  const handleInitialLoadComplete = () => {
    setShowInitialLoading(false);
  };

  const loadSharedResult = async (sharedTestId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/share/${sharedTestId}`);
      setAnalysisResult(response.data.result);
      setResultImageUrl(response.data.imageUrl);
      setTestId(sharedTestId);
    } catch (error) {
      setError('无法加载分享的测试结果');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedImage(file);
      setAnalysisResult(null);
      setResultImageUrl(null);
      setError(null);
      setShareUrl(null);
      setTestId(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  const analyzeImage = async () => {
    if (!uploadedImage) return;

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('image', uploadedImage);

    try {
      const response = await axios.post('/api/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setAnalysisResult(response.data.result);
      setResultImageUrl(response.data.imageUrl);
      setShareUrl(response.data.shareUrl);
      setTestId(response.data.testId);
      
      // 更新URL但不刷新页面
      window.history.pushState({}, '', `/share/${response.data.testId}`);
    } catch (error) {
      setError(error.response?.data?.error || '分析失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const resetTest = () => {
    setUploadedImage(null);
    setAnalysisResult(null);
    setResultImageUrl(null);
    setError(null);
    setShareUrl(null);
    setTestId(null);
    window.history.pushState({}, '', '/');
  };

  return (
    <div className="App">
      {showInitialLoading && (
        <LoadingSpinner 
          isInitialLoad={true} 
          onLoadComplete={handleInitialLoadComplete}
          targetRef={logoRef}
        />
      )}
      <div className="container">
        <header className="header">
          <img src="/whatsmeme.png" alt="Whatsmeme" className="logo" ref={logoRef} />
          <h1 className="title rubik-bubbles-regular">Whatsmeme</h1>
          <p className="subtitle">上传你最爱的表情包，AI来告诉你隐藏的性格密码！</p>
          <p className="subtitle">📋 支持拖拽、选择文件或直接粘贴图片</p>
        </header>

        {!analysisResult && (
          <div className="upload-section">
            <div
              {...getRootProps()}
              className={`dropzone ${isDragActive ? 'active' : ''} ${uploadedImage ? 'has-image' : ''}`}
            >
              <input {...getInputProps()} />
              {uploadedImage ? (
                <div className="image-preview">
                  <img
                    src={URL.createObjectURL(uploadedImage)}
                    alt="预览"
                    className="preview-image"
                  />
                  <p className="image-name">{uploadedImage.name}</p>
                </div>
              ) : (
                <div className="upload-prompt">
                  <div className="upload-icon">📸</div>
                  <p>
                    {isDragActive
                      ? '放开鼠标，上传表情包！'
                      : '拖拽表情包到这里，或点击选择文件'}
                  </p>
                  <p className="upload-hint">支持 JPG、PNG、GIF 格式，最大 5MB</p>
                  <p className="upload-hint">💡 也可以直接 <strong>Ctrl+V</strong> 粘贴图片哦！</p>
                </div>
              )}
            </div>

            {uploadedImage && (
              <div className="action-buttons">
                <button
                  onClick={analyzeImage}
                  disabled={loading}
                  className="analyze-btn"
                >
                  {loading ? '正在分析中...' : '开始测试 🔮'}
                </button>
                <button onClick={resetTest} className="reset-btn">
                  重新选择
                </button>
              </div>
            )}
          </div>
        )}

        {loading && <LoadingSpinner isInitialLoad={false} />}

        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
            <button onClick={resetTest} className="retry-btn">
              重新开始
            </button>
          </div>
        )}

        {analysisResult && (
          <div className="result-section">
            <div className="result-card">
              <h2 className="result-title">🎭 你的人格分析结果</h2>
              
              {/* 显示表情包图片 */}
              {resultImageUrl && (
                <div className="result-image-container">
                  <img 
                    src={resultImageUrl} 
                    alt="你的表情包" 
                    className="result-image"
                  />
                </div>
              )}
              
              {/* 显示 Markdown 格式的分析结果 */}
              <div className="result-content">
                <div className="markdown-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                  >
                    {analysisResult}
                  </ReactMarkdown>
                </div>
              </div>
              
              {shareUrl && (
                <div className="share-section">
                  <h3>分享给朋友们 ✨</h3>
                  <ShareButtons url={shareUrl} />
                </div>
              )}
              
              <div className="action-buttons">
                <button onClick={resetTest} className="new-test-btn">
                  再测一次 🔄
                </button>
              </div>
            </div>
          </div>
        )}

        <footer className="footer">
          <p>💡 每个表情包都藏着你的小秘密</p>
          <p className="disclaimer">*本测试仅供娱乐，不构成专业心理建议</p>
        </footer>
      </div>
    </div>
  );
}

export default App;